/**
 * 角色与权限的**前端唯一出处**（v0.47）。
 *
 * ⚠️ 这是后端 `backend/db/models.py`（`ROLE_*` / `ROLE_LEVEL` / `ROLE_CN`）
 *    在中前端的镜像。跨语言没法共享代码，所以只能靠测试钉死：
 *    `backend/tests/test_auth_v047.py::TestRoleTableParity` 会把本文件读成文本、
 *    把下面四组常量抠出来与 Python 侧逐条比对。**改这里必须同时改那边**，
 *    否则那条用例会红 —— 这是防止两份表悄悄漂移的唯一手段。
 *
 * ★ 前端这份表**只用来决定界面显不显示**，不是安全边界：
 *   真正的拦截在后端中间件里。界面藏了按钮但后端没拦，等于没拦；
 *   反过来（界面显示、后端拦）才是安全的失败方向 —— 用户看到"权限不足"提示，
 *   而不是以为操作成功了。
 */

export const ROLE_ADMIN = 'admin';
export const ROLE_ENGINEER = 'engineer';
export const ROLE_INSPECTOR = 'inspector';
export const ROLE_VIEWER = 'viewer';

export const USER_ROLES = [ROLE_ADMIN, ROLE_ENGINEER, ROLE_INSPECTOR, ROLE_VIEWER] as const;
export type Role = (typeof USER_ROLES)[number];

/** 线性等级：viewer(1) ⊂ inspector(2) ⊂ engineer(3) ⊂ admin(4)。 */
export const ROLE_LEVEL: Record<Role, number> = {
  viewer: 1,
  inspector: 2,
  engineer: 3,
  admin: 4,
};

export const ROLE_CN: Record<Role, string> = {
  admin: '管理员',
  engineer: '工程师',
  inspector: '检验员',
  viewer: '查看者',
};

/** 每个角色干什么 —— 用户管理页里要显示，免得管理员凭猜分配。 */
export const ROLE_DESC: Record<Role, string> = {
  admin: '全部功能 + 账号管理',
  engineer: '上传图纸、识别、编辑特征、文件夹、零件主数据与 BOM',
  inspector: '在工程师基础上少一层：可录实测数据/测量报告，不能改图纸与特征',
  viewer: '只看 + 导出报告，不能改动任何数据',
};

export function roleLabel(role?: string | null): string {
  return (role && (ROLE_CN as Record<string, string>)[role]) || role || '未知';
}

export function roleLevel(role?: string | null): number {
  return (role && (ROLE_LEVEL as Record<string, number>)[role]) || 0;
}

/** `role` 是否达到 `min`。未知/空角色按 0（比 viewer 还低）⇒ 什么写操作都不给。 */
export function roleAtLeast(role: string | null | undefined, min: Role): boolean {
  return roleLevel(role) >= ROLE_LEVEL[min];
}
